import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Menu, X, LogOut, User, ChevronDown, HelpCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import UserGuideModal from '../dashboard/components/UserGuideModal';
import { NotificationBell } from '../notifications';
import Sidebar from './Sidebar';

const Layout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const role = user?.role || 'student';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileDropdownOpen && !event.target.closest('.profile-dropdown')) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileDropdownOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsProfileDropdownOpen(false);
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Modern Header */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300
        ${isScrolled ? 'bg-white/98 backdrop-blur-md shadow-lg' : 'bg-white/95 backdrop-blur-sm shadow-md'}`}>
        <div className="px-4 sm:px-6 mx-auto">
          <div className="flex items-center justify-between h-[70px]">
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 lg:hidden transition-all duration-200"
                aria-label="Toggle sidebar"
              >
                {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              <Link to="/" className="flex items-center hover:scale-105 transition-transform duration-200">
                <img
                  src="/uzimalogo.jpg"
                  alt="Uzima Logo"
                  className="h-10 w-auto"
                  onError={(e) => {
                    e.target.src = "/uzimalogo.jpg";
                  }}
                />
                <div className="ml-3">
                  <div className="text-xl font-bold bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 bg-clip-text text-transparent">
                    Uzima
                  </div>
                  <div className="text-[0.7rem] font-semibold text-orange-500">
                    Counseling Center
                  </div>
                </div>
              </Link>
            </div>

            <div className="flex items-center gap-3 sm:gap-4">
              {/* Notification Bell */}
              <NotificationBell />

              {/* Help Icon */}
              <button
                onClick={() => setIsGuideModalOpen(true)}
                className="p-2 rounded-xl text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200"
                aria-label="Help"
              >
                <HelpCircle size={20} />
              </button>

              <div className="relative profile-dropdown">
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 hover:border-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-uzima-maroon-600 to-uzima-maroon-700 flex items-center justify-center text-white font-semibold text-sm">
                    {(user?.fullName || user?.username)?.charAt(0).toUpperCase()}
                  </div>
                  <ChevronDown size={16} className={`text-gray-500 transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in slide-in-from-top-2 duration-200">
                    <div className="px-4 py-3 bg-gradient-to-br from-gray-50 to-gray-100 border-b border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-uzima-maroon-600 to-uzima-maroon-700 flex items-center justify-center text-white font-bold text-lg">
                          {(user?.fullName || user?.username)?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{user?.fullName || user?.username}</div>
                          <div className="text-xs text-gray-500 capitalize">{user?.role}</div>
                        </div>
                      </div>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={() => handleNavigation(`/${role}/profile`)}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150"
                      >
                        <User size={16} />
                        <span>My Profile</span>
                      </button>
                      <div className="my-1 h-px bg-gray-100" />
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                      >
                        <LogOut size={16} />
                        <span>Sign out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex pt-[70px]">
        {/* Modern Sidebar Component */}
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          role={role}
        />

        {/* Main content */}
        <main className={`flex-1 min-h-[calc(100vh-70px)] transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'
        }`}>
          <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
            {children}
          </div>
        </main>
      </div>
      {/* User Guide Modal */}
      <UserGuideModal
        isOpen={isGuideModalOpen}
        onClose={() => setIsGuideModalOpen(false)}
        role={role}
      />
    </div>
  );
};

export default Layout;